import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CollapseComponent, EmptyStateComponent } from '@tecalisdev/infinity';
import { LanguageService } from '../../../../../core/services/language.service';
import {
	IOperationDetailResponse,
	IOperationFormDetail,
	ITextLocaleMap,
	OPERATION_STATUS,
} from '../../../../models/operations.models';
import {
	groupFormOperationAnswersBySection,
	IFormOperationDetailSectionView,
} from '../../../../utils/form-operation-detail-sections.util';
import { resolveWorkflowLocaleText } from '../../../../../workflows/utils/workflow-locale-text.util';
import { FormOperationDetailSectionsComponent } from '../../../form-operation-detail-sections/form-operation-detail-sections.component';
import { OperationDetailOperationMetaComponent } from '../../../../../shared/components/operations/common/operation-detail-operation-meta/operation-detail-operation-meta.component';
import { resolveFormMatchDataErrors, resolveFormOperationEffectiveState } from '../../../../utils/form-operation-match-data.util';
import { NOT_AVAILABLE_DISPLAY } from '../../../../../core/utils/utils';

type FormDetailsCollapseSection = 'data' | 'operation';

/**
 * Detalle de operación FORM (pestaña Datos).
 * Agrupa respuestas por secciones Figma (datos personales, documento, dirección, otros)
 * y muestra metadatos de la operación en un segundo acordeón.
 */
@Component({
	selector: 'app-operation-detail-tabs-details-form',
	imports: [
		TranslatePipe,
		CollapseComponent,
		EmptyStateComponent,
		FormOperationDetailSectionsComponent,
		OperationDetailOperationMetaComponent,
	],
	templateUrl: './operation-detail-tabs-details-form.component.html',
	styleUrl: './operation-detail-tabs-details-form.component.scss',
})
export class OperationDetailTabsDetailsFormComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	private languageService = inject(LanguageService);

	public OPERATION_STATUS = OPERATION_STATUS;
	public formData: IOperationFormDetail | null = null;
	/** Secciones del acordeón «Datos», derivadas de `answers[]`. */
	public dataSections: IFormOperationDetailSectionView[] = [];

	/** Mismo criterio acordeón que documentos / OCR en detalle de operación. */
	public expandedCollapseSection: FormDetailsCollapseSection | null = 'data';

	public get displayState(): OPERATION_STATUS | string | undefined {
		return resolveFormOperationEffectiveState(this.formData);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) {
			return;
		}
		this.formData = this.detail?.data ? (this.detail.data as unknown as IOperationFormDetail) : null;
		this.dataSections = groupFormOperationAnswersBySection(
			this.formData?.answers,
			this.formData?.config,
			resolveFormMatchDataErrors(this.formData),
		);
	}

	public toggleCollapse(section: FormDetailsCollapseSection, expanded?: boolean): void {
		this.expandedCollapseSection = !expanded ? null : section;
	}

	/** Texto localizado (mapa i18n o string) según el idioma activo del panel. */
	public resolveLocaleText(value: string | ITextLocaleMap | null | undefined): string {
		this.languageService.currentLang();
		return resolveWorkflowLocaleText(value, this.languageService.getLanguage());
	}

	/** Misma regla que el meta FORM histórico: 0 intentos → «1». */
	public formTriesLabel(): string {
		const totalTries = this.formData?.total_tries ?? this.detail?.total_tries;
		if (totalTries === 0) return '1';
		if (totalTries == null) return NOT_AVAILABLE_DISPLAY;
		return totalTries.toString();
	}
}
